import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, message, Modal, Select, Switch, Table, Tag, Checkbox, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FilePdfOutlined } from '@ant-design/icons';
import { CheckCircleIcon, Cog6ToothIcon, DocumentTextIcon, ShieldCheckIcon, SparklesIcon, KeyIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import StudioNavBar from '../components/common/StudioNavBar';
import { useAuth } from '../context/AuthContext';
import { authAPI, dashboardAPI, expenseAPI, paymentAPI, staffAPI, projectAPI, studioRoomAPI } from '../services/api';
import { formatCurrencySimple } from '../utils/currency';
import { DEFAULT_CURRENCY_CODE } from '../constants/localeOptions';
import { downloadStaffPaymentReceiptPdf } from '../utils/staffPaymentReceipt';

const accentOptions = [
  { label: 'Indigo', value: 'indigo' },
  { label: 'Emerald', value: 'emerald' },
  { label: 'Rose', value: 'rose' },
  { label: 'Amber', value: 'amber' },
];

const densityOptions = [
  { label: 'Comfortable', value: 'comfortable' },
  { label: 'Compact', value: 'compact' },
  { label: 'Spacious', value: 'spacious' },
];

const defaultReports = [
  { key: 'expenses', label: 'Monthly expenses summary' },
  { key: 'payments', label: 'Payments received overview' },
  { key: 'status', label: 'Financial status & cashflow' },
  { key: 'Staffs', label: 'Staff commissions breakdown' },
];

const Management: React.FC = () => {
  const { user, setUser } = useAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [isDark, setIsDark] = useState(false);
  const [accent, setAccent] = useState('indigo');
  const [density, setDensity] = useState('comfortable');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedReports, setSelectedReports] = useState<string[]>(['expenses', 'payments', 'status']);
  const [subscriptionPlan, setSubscriptionPlan] = useState('free');
  const [StaffCount, setStaffCount] = useState(0);
  const [Staffs, setStaffs] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [studioRooms, setStudioRooms] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<any | null>(null);
  const [roomForm] = Form.useForm();
  const [staffPaymentModalOpen, setStaffPaymentModalOpen] = useState(false);
  const [staffPayments, setStaffPayments] = useState<any[]>([]);
  const [paymentForm] = Form.useForm();
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const selectedPaymentStaffId = Form.useWatch('staff_id', paymentForm);
  
  // Staff access management state
  const [accessKeyModalOpen, setAccessKeyModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [generatedKey, setGeneratedKey] = useState<string>('');
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState<string[]>([]);

  // Available sections for Staff access
  const availableSections = [
    { key: 'dashboard', label: 'Dashboard', description: 'View studio overview and metrics' },
    { key: 'projects', label: 'Projects', description: 'View and manage projects' },
    { key: 'payments', label: 'Payments', description: 'View and record payments' },
    { key: 'expenses', label: 'Expenses', description: 'View and add expenses' },
    { key: 'staff', label: 'Staff', description: 'View Staff directory' },
  ];

  useEffect(() => {
    const storedTheme = localStorage.getItem('soms_theme');
    const storedAccent = localStorage.getItem('soms_accent');
    const storedDensity = localStorage.getItem('soms_density');

    if (storedTheme === 'dark') {
      document.documentElement.classList.add('dark');
      setIsDark(true);
    }

    if (storedAccent) {
      setAccent(storedAccent);
      document.documentElement.setAttribute('data-accent', storedAccent);
    } else {
      document.documentElement.setAttribute('data-accent', 'indigo');
    }

    if (storedDensity) {
      setDensity(storedDensity);
      document.documentElement.setAttribute('data-density', storedDensity);
    } else {
      document.documentElement.setAttribute('data-density', 'comfortable');
    }
  }, []);

  useEffect(() => {
    if (user) {
      profileForm.setFieldsValue({
        studio_name: user.studio_name ?? '',
        username: user.username ?? '',
      });
    }
  }, [user, profileForm]);

  useEffect(() => {
    const fetchManagementData = async () => {
      setIsLoadingData(true);
      try {
        const [expenseRes, paymentRes, projectRes, StaffRes, dashboardRes, roomRes, staffPaymentRes] = await Promise.all([
          expenseAPI.getExpenses(),
          paymentAPI.getPayments(),
          projectAPI.getProjects(),
          staffAPI.getStaff(),
          dashboardAPI.getMetrics(),
          studioRoomAPI.getRooms(),
          paymentAPI.getStaffPayments()
        ]);

        setExpenses(expenseRes.data || []);
        setPayments(paymentRes.data || []);
        setProjects(projectRes.data || []);
        setStaffs(StaffRes.data || []);
        setStudioRooms(roomRes.data || []);
        setStaffPayments(staffPaymentRes?.data || []);
        setStaffCount((StaffRes.data || []).length);
        setSubscriptionPlan(dashboardRes.data?.subscription_status || user?.subscription_plan || 'free');
      } catch (error) {
        console.error('Failed to load management data:', error);
        message.error('Failed to load management data. Please try again.');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchManagementData();
  }, [user]);

  const handleThemeToggle = (checked: boolean) => {
    setIsDark(checked);
    if (checked) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('soms_theme', 'dark');
      message.success('Dark mode enabled');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('soms_theme', 'light');
      message.success('Light mode enabled');
    }
  };

  const handleProfileSave = async (values: any) => {
    try {
      const response = await authAPI.updateProfile({
        studio_name: values.studio_name,
        username: values.username,
      });
      const updatedUser = response.data;
      setUser(updatedUser as any);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      message.success('Profile settings updated');
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handlePasswordSave = async (values: any) => {
    if (values.new_password !== values.confirm_password) {
      message.error('New password and confirmation do not match');
      return;
    }

    try {
      await authAPI.changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      message.success('Password updated successfully');
      passwordForm.resetFields();
    } catch (error: any) {
      const backendMessage = error?.response?.data?.current_password?.[0];
      message.error(backendMessage || 'Failed to update password');
    }
  };

  // Staff access management handlers
  const handleShowAccessKey = (Staff: any) => {
    setSelectedStaff(Staff);
    setGeneratedKey(Staff.access_key || '');
    setAccessKeyModalOpen(true);
  };

  const handleRegenerateKey = async () => {
    if (!selectedStaff) return;
    
    try {
      const response = await staffAPI.regenerateAccessKey(selectedStaff.id);
      setGeneratedKey(response.data.access_key);
      message.success('Access key regenerated successfully');
      
      // Refresh Staffs list
      const StaffRes = await staffAPI.getStaff();
      setStaffs(StaffRes.data || []);
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Failed to regenerate access key');
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(generatedKey);
    message.success('Access key copied to clipboard');
  };

  const handleOpenPermissions = (Staff: any) => {
    setSelectedStaff(Staff);
    setEditingPermissions(Staff.allowed_sections || ['dashboard', 'projects']);
    setPermissionsModalOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedStaff) return;
    
    try {
      await staffAPI.updateStaff(selectedStaff.id, {
        allowed_sections: editingPermissions,
      });
      message.success('Permissions updated successfully');
      setPermissionsModalOpen(false);
      
      // Refresh Staffs list
      const StaffRes = await staffAPI.getStaff();
      setStaffs(StaffRes.data || []);
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const togglePermission = (sectionKey: string) => {
    setEditingPermissions((prev) => {
      if (prev.includes(sectionKey)) {
        // Don't allow removing dashboard
        if (sectionKey === 'dashboard') {
          message.warning('Dashboard access cannot be removed');
          return prev;
        }
        return prev.filter((s) => s !== sectionKey);
      }
      return [...prev, sectionKey];
    });
  };

  const openCreateRoomModal = () => {
    setEditingRoom(null);
    roomForm.resetFields();
    setRoomModalOpen(true);
  };

  const openEditRoomModal = (room: any) => {
    setEditingRoom(room);
    roomForm.setFieldsValue({ name: room.name });
    setRoomModalOpen(true);
  };

  const handleSaveRoom = async () => {
    try {
      const values = await roomForm.validateFields();
      const payload = { name: values.name };
      if (editingRoom) {
        await studioRoomAPI.updateRoom(editingRoom.id, payload);
        message.success('Studio room updated successfully');
      } else {
        await studioRoomAPI.createRoom(payload);
        message.success('Studio room added successfully');
      }
      const roomRes = await studioRoomAPI.getRooms();
      setStudioRooms(roomRes.data || []);
      setRoomModalOpen(false);
      roomForm.resetFields();
      setEditingRoom(null);
    } catch (error: any) {
      if (error?.errorFields) return;
      const backendMessage = error?.response?.data?.name?.[0] || error?.response?.data?.detail;
      message.error(backendMessage || 'Failed to save studio room');
    }
  };

  const getStaffDisplayName = (staff: any) => {
    if (!staff) return 'Team member';
    return `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.username || 'Team member';
  };

  const getApprovedPayoutTotal = (staffId: number) => staffPayments
    .filter((payment: any) => Number(payment.recipient) === Number(staffId) && payment.status === 'approved')
    .reduce((sum: number, payment: any) => sum + Number(payment.amount || 0), 0);

  const getStaffOwedAmount = (staff: any) => {
    if (!staff) return 0;
    const role = String(staff.role || '').toLowerCase();

    if (role === 'producer') {
      const projectIds = new Set(
        projects
          .filter((project: any) => Number(project.assigned_producer) === Number(staff.id))
          .map((project: any) => Number(project.id))
      );
      const producerPaid = payments
        .filter((payment: any) => projectIds.has(Number(payment.project)))
        .reduce((sum: number, payment: any) => sum + Number(payment.amount || 0), 0);
      return producerPaid * (Number(staff.commission_rate || 0) / 100);
    }

    return Number(staff.salary || 0);
  };

  const getStaffRemainingAmount = (staff: any) => Math.max(0, getStaffOwedAmount(staff) - getApprovedPayoutTotal(Number(staff?.id)));

  const getStaffCurrencyCode = (staff: any) => staff?.salary_currency || DEFAULT_CURRENCY_CODE;

  const selectedPaymentStaff = Staffs.find((staff: any) => Number(staff.id) === Number(selectedPaymentStaffId));
  const selectedPaymentRemaining = getStaffRemainingAmount(selectedPaymentStaff);
  const selectedPaymentCurrency = getStaffCurrencyCode(selectedPaymentStaff);

  const applyPaymentPortion = (portion: string, staff = selectedPaymentStaff) => {
    if (!staff || portion === 'custom') return;
    const remaining = getStaffRemainingAmount(staff);
    paymentForm.setFieldValue('amount', portion === 'half' ? Number((remaining / 2).toFixed(2)) : Number(remaining.toFixed(2)));
  };

  const handleOpenPaymentModal = () => {
    paymentForm.resetFields();
    paymentForm.setFieldsValue({ payment_portion: 'custom' });
    setStaffPaymentModalOpen(true);
  };

  const handleSubmitPayment = async () => {
    try {
      const values = await paymentForm.validateFields();
      const staff = Staffs.find((item: any) => Number(item.id) === Number(values.staff_id));
      const remaining = getStaffRemainingAmount(staff);
      if (Number(values.amount || 0) > remaining) {
        message.error(`Amount cannot exceed the remaining balance (${formatCurrencySimple(remaining, getStaffCurrencyCode(staff))})`);
        return;
      }
      setIsSubmittingPayment(true);
      await paymentAPI.createStaffPayment({
        recipient: values.staff_id,
        amount: values.amount,
        currency_code: getStaffCurrencyCode(staff),
        month_year: values.month_year || '',
        notes: values.notes || '',
      });
      message.success('Staff payment recorded and receipt generated');
      
      const result = await paymentAPI.getStaffPayments();
      setStaffPayments(result.data || []);
      setStaffPaymentModalOpen(false);
      paymentForm.resetFields();
    } catch(err: any) { message.error(err?.response?.data?.detail || 'Failed to record staff payment'); }
    finally { setIsSubmittingPayment(false); }
  };

  const handleDeleteRoom = async (id: number) => {
    try {
      await studioRoomAPI.deleteRoom(id);
      message.success('Studio room deleted successfully');
      const roomRes = await studioRoomAPI.getRooms();
      setStudioRooms(roomRes.data || []);
    } catch (error: any) {
      const backendMessage = error?.response?.data?.detail;
      message.error(backendMessage || 'Failed to delete studio room');
    }
  };

  const reportTitle = useMemo(() => {
    if (!selectedMonth) return 'Monthly studio report';
    return `Monthly studio report - ${selectedMonth}`;
  }, [selectedMonth]);

  const [reportExpenses, reportPayments, reportProjects] = useMemo(() => {
    if (!selectedMonth) return [expenses, payments, projects];

    const [year, month] = selectedMonth.split('-').map(Number);
    const inMonth = (dateValue?: string) => {
      if (!dateValue) return false;
      const date = new Date(dateValue);
      return date.getFullYear() === year && date.getMonth() + 1 === month;
    };

    return [
      expenses.filter((item) => inMonth(item.expense_date)),
      payments.filter((item) => inMonth(item.payment_date)),
      projects,
    ];
  }, [expenses, payments, projects, selectedMonth]);

  const reportTotals = useMemo(() => {
    const totalExpenses = reportExpenses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalPayments = reportPayments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const net = totalPayments - totalExpenses;
    return {
      totalExpenses,
      totalPayments,
      net,
    };
  }, [reportExpenses, reportPayments]);

  const handleGenerateReport = () => {
    if (!selectedMonth) {
      message.warning('Pick a month to generate your report');
      return;
    }

    if (selectedReports.length === 0) {
      message.warning('Select at least one report section');
      return;
    }

    const printable = window.open('', '_blank', 'width=900,height=650');
    if (!printable) {
      message.error('Pop-up blocked. Allow pop-ups to generate the PDF.');
      return;
    }

    const sections = defaultReports
      .filter((item) => selectedReports.includes(item.key))
      .map((item) => `<li style="margin-bottom:8px;">${item.label}</li>`)
      .join('');

    const formatUGX = (value: number) => `UGX ${Number(value).toLocaleString('en-US')}`;

    const topExpenses = reportExpenses
      .slice()
      .sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
      .slice(0, 5)
      .map((item: any) => {
        const description = item.description || 'Expense';
        return `<tr><td>${description}</td><td style="text-align:right;">${formatUGX(item.amount || 0)}</td></tr>`;
      })
      .join('');

    const topPayments = reportPayments
      .slice()
      .sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
      .slice(0, 5)
      .map((item: any) => {
        const label = item.project_name || item.project_custom_id || 'Payment';
        return `<tr><td>${label}</td><td style="text-align:right;">${formatUGX(item.amount || 0)}</td></tr>`;
      })
      .join('');

    const StaffCommissions = Staffs
      .map((Staff: any) => {
        const assignedProjects = projects.filter((p) => p.assigned_Staff === Staff.id || p.assigned_Staff_name === `${Staff.first_name} ${Staff.last_name}`);
        const totalPaid = assignedProjects.reduce((sum, p) => sum + Number(p.total_paid || 0), 0);
        
        let compensation = 0;
        let compFormatted = '';

        if (Staff.role === 'staff') {
            compensation = Number(Staff.salary || 0);
            compFormatted = formatCurrencySimple(compensation, Staff.salary_currency || DEFAULT_CURRENCY_CODE) + ' (Salary)';
        } else {
            compensation = (Number(Staff.commission_rate || 0) / 100) * totalPaid;
            compFormatted = formatCurrencySimple(compensation, DEFAULT_CURRENCY_CODE) + ' (Commission)';
        }

        return {
          name: `${Staff.first_name || ''} ${Staff.last_name || ''}`,
          compensation,
          compFormatted,
          projects: assignedProjects.length,
          role: Staff.role
        };
      })
      .filter((p) => p.compensation > 0 || p.projects > 0)
      .sort((a, b) => b.compensation - a.compensation)
      .slice(0, 8);

    const summaryBlock = selectedReports.includes('status')
      ? `
          <div class="block">
            <h2>Summary</h2>
            <p>Total payments: <strong>${formatUGX(reportTotals.totalPayments)}</strong></p>
            <p>Total expenses: <strong>${formatUGX(reportTotals.totalExpenses)}</strong></p>
            <p>Net position: <strong>${formatUGX(reportTotals.net)}</strong></p>
            <p>Projects tracked: <strong>${reportProjects.length}</strong></p>
            <p>Staffs in studio: <strong>${StaffCount}</strong></p>
          </div>
        `
      : '';

    const expensesBlock = selectedReports.includes('expenses')
      ? `
          <div class="block">
            <h2>Top expenses</h2>
            <table style="width:100%; border-collapse: collapse;">
              <thead>
                <tr><th style="text-align:left; padding:6px 0;">Expense</th><th style="text-align:right; padding:6px 0;">Amount</th></tr>
              </thead>
              <tbody>${topExpenses || '<tr><td colspan="2">No expenses found.</td></tr>'}</tbody>
            </table>
          </div>
        `
      : '';

    const paymentsBlock = selectedReports.includes('payments')
      ? `
          <div class="block">
            <h2>Top payments</h2>
            <table style="width:100%; border-collapse: collapse;">
              <thead>
                <tr><th style="text-align:left; padding:6px 0;">Project</th><th style="text-align:right; padding:6px 0;">Amount</th></tr>
              </thead>
              <tbody>${topPayments || '<tr><td colspan="2">No payments found.</td></tr>'}</tbody>
            </table>
          </div>
        `
      : '';

    const StaffsBlock = selectedReports.includes('Staffs')
      ? `
          <div class="block">
            <h2>Team Compensation</h2>
            ${StaffCommissions.length > 0
              ? `<table style="width:100%; border-collapse:collapse;">
                  <thead>
                    <tr style="background-color:#f3f4f6; border-bottom:1px solid #d1d5db;">
                      <th style="padding:8px; text-align:left; font-weight:600;">Staff</th>
                      <th style="padding:8px; text-align:center; font-weight:600;">Projects</th>
                      <th style="padding:8px; text-align:right; font-weight:600;">Compensation</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${StaffCommissions
                      .map(
                        (p) =>
                          `<tr style="border-bottom:1px solid #e5e7eb;">
                          <td style="padding:8px;">${p.name}</td>
                          <td style="padding:8px; text-align:center;">${p.projects}</td>
                          <td style="padding:8px; text-align:right;">${p.compFormatted}</td>
                        </tr>`
                      )
                      .join('')}
                  </tbody>
                </table>`
              : '<p>No team members with assignments found.</p>'}
          </div>
        `
      : '';

    printable.document.write(`
      <html>
        <head>
          <title>${reportTitle}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 32px; color: #0f172a; }
            h1 { font-size: 28px; margin-bottom: 8px; }
            h2 { font-size: 18px; margin-top: 24px; }
            p { color: #475569; }
            ul { padding-left: 20px; }
            .badge { display: inline-block; padding: 4px 10px; background: #eef2ff; color: #4338ca; border-radius: 999px; font-size: 12px; }
            .block { background: #f8fafc; padding: 16px; border-radius: 12px; margin-top: 16px; }
          </style>
        </head>
        <body>
          <span class="badge">SOMS Report</span>
          <h1>${reportTitle}</h1>
          <p>Generated for ${user?.studio_name ?? user?.username ?? 'your studio'}.</p>
          <div class="block">
            <h2>Included Sections</h2>
            <ul>${sections}</ul>
          </div>
          ${summaryBlock}
          ${expensesBlock}
          ${paymentsBlock}
          ${StaffsBlock}
          <div class="block">
            <h2>Next Steps</h2>
            <p>Open this report in your PDF viewer or use Print → Save as PDF.</p>
          </div>
        </body>
      </html>
    `);
    printable.document.close();
    printable.focus();
    printable.print();
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <StudioNavBar />

      <main className="mx-auto max-w-7xl space-y-10 px-4 py-8 sm:px-6 lg:px-8">
        <section className="rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-600 p-8 text-white shadow-2xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-xs font-semibold uppercase tracking-[0.2em]">
                <SparklesIcon className="h-4 w-4" />
                Management Suite
              </div>
              <h1 className="mt-4 text-3xl font-bold sm:text-4xl">Studio Management</h1>
              <p className="mt-2 max-w-2xl text-sm text-indigo-100">
                Control your studio identity, experience, and reporting workflows with a polished, enterprise-ready
                control room built for music professionals.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {[
                { label: 'Current Plan', value: subscriptionPlan ? subscriptionPlan.toUpperCase() : 'FREE' },
                { label: 'Staffs', value: StaffCount.toString() },
                { label: 'Next Report', value: selectedMonth || 'Pick Month' },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl bg-white/15 px-4 py-4 text-center backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.2em] text-indigo-100">{item.label}</p>
                  <p className="mt-2 text-lg font-semibold">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-200">
                <Cog6ToothIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Appearance</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Tailor the studio workspace look and feel.</p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
                <div>
                  <p className="text-sm font-medium">Dark mode</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Reduce glare for late sessions.</p>
                </div>
                <Switch checked={isDark} onChange={handleThemeToggle} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Accent color</label>
                <Select
                  className="mt-2 w-full"
                  size="large"
                  options={accentOptions}
                  value={accent}
                  onChange={(value) => {
                    setAccent(value);
                    localStorage.setItem('soms_accent', value);
                    document.documentElement.setAttribute('data-accent', value);
                  }}
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Layout density</label>
                <Select
                  className="mt-2 w-full"
                  size="large"
                  options={densityOptions}
                  value={density}
                  onChange={(value) => {
                    setDensity(value);
                    localStorage.setItem('soms_density', value);
                    document.documentElement.setAttribute('data-density', value);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:col-span-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-200">
                <CheckCircleIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Studio identity</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Update studio name and display username.</p>
              </div>
            </div>

            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileSave}
              className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <Form.Item
                name="studio_name"
                label="Studio name"
                rules={[{ required: true, message: 'Studio name is required' }]}
              >
                <Input placeholder="Enter studio name" size="large" />
              </Form.Item>
              <Form.Item
                name="username"
                label="Username"
                rules={[{ required: true, message: 'Username is required' }]}
              >
                <Input placeholder="Enter username" size="large" />
              </Form.Item>
              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                <span>Changes apply immediately and are stored in your studio profile.</span>
                <Button type="primary" htmlType="submit" size="large">
                  Save identity
                </Button>
              </div>
            </Form>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 lg:col-span-2">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                <ShieldCheckIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Security & access</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Change your password and secure the studio.</p>
              </div>
            </div>

            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordSave}
              className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <Form.Item
                name="current_password"
                label="Current password"
                rules={[{ required: true, message: 'Enter your current password' }]}
              >
                <Input.Password placeholder="Enter current password" size="large" />
              </Form.Item>
              <Form.Item
                name="new_password"
                label="New password"
                rules={[{ required: true, message: 'Enter a new password' }]}
              >
                <Input.Password placeholder="Enter new password" size="large" />
              </Form.Item>
              <Form.Item
                name="confirm_password"
                label="Confirm new password"
                rules={[{ required: true, message: 'Confirm new password' }]}
              >
                <Input.Password placeholder="Confirm new password" size="large" />
              </Form.Item>
              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                <span>We recommend strong passwords with 12+ characters and symbols.</span>
                <Button type="primary" htmlType="submit" size="large">
                  Update password
                </Button>
              </div>
            </Form>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-200">
                <SparklesIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Subscription</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Plans will be published by the super user.</p>
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {[
                { name: 'Studio Starter', desc: 'Core tracking + 1 user seat' },
                { name: 'Studio Pro', desc: 'Advanced reporting + 5 seats' },
                { name: 'Enterprise', desc: 'Custom automation & support' },
              ].map((plan) => (
                <div key={plan.name} className="rounded-xl border border-dashed border-slate-200 p-4 dark:border-slate-700">
                  <p className="text-sm font-semibold">{plan.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{plan.desc}</p>
                  <Button className="mt-3" block disabled>
                    Coming soon
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Studio Rooms</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Add and maintain room options used during project scheduling.
              </p>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateRoomModal}>
              Add Room
            </Button>
          </div>

          <Table
            rowKey="id"
            loading={isLoadingData}
            dataSource={studioRooms}
            pagination={{ pageSize: 6 }}
            locale={{ emptyText: 'No studio rooms yet. Add one to enable scheduling dropdown selection.' }}
            columns={[
              {
                title: 'Room Name',
                dataIndex: 'name',
                key: 'name',
                render: (value) => <span className="font-medium">{value}</span>,
              },
              {
                title: 'Created',
                dataIndex: 'created_at',
                key: 'created_at',
                render: (value) => new Date(value).toLocaleDateString(),
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 160,
                render: (_, record) => (
                  <div className="flex items-center gap-2">
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEditRoomModal(record)}>
                      Edit
                    </Button>
                    <Popconfirm
                      title="Delete room?"
                      description="This room will no longer appear in scheduling dropdown options."
                      okText="Delete"
                      cancelText="Cancel"
                      onConfirm={() => handleDeleteRoom(record.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />}>
                        Delete
                      </Button>
                    </Popconfirm>
                  </div>
                ),
              },
            ]}
          />
        </section>

        {/* Staff Access Management Section */}
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-200">
                <UserGroupIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Team Access Management</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Manage login keys and permissions for your studio Staffs and staff.</p>
              </div>
            </div>
          </div>

          {Staffs.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <UserGroupIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No team members in your studio yet.</p>
              <p className="text-xs mt-1">Add staff or Staffs from the Staffs page to manage their access here.</p>
            </div>
          ) : (
            <Table
              rowKey="id"
              loading={isLoadingData}
              dataSource={Staffs}
              pagination={{ pageSize: 5 }}
              columns={[
                {
                  title: 'Team Member',
                  render: (_, record) => (
                    <div>
                      <p className="font-medium">{record.first_name} {record.last_name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{record.role === 'staff' && record.staff_title ? record.staff_title : (record.role === 'producer' ? 'Producer' : 'Staff')} • {record.email}</p>
                    </div>
                  ),
                },
                {
                  title: 'Access Key',
                  render: (_, record) => (
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs font-mono">
                        {record.access_key ? `${record.access_key.slice(0, 4)}****` : 'Not set'}
                      </code>
                      <Button 
                        size="small" 
                        type="link"
                        onClick={() => handleShowAccessKey(record)}
                      >
                        {record.access_key ? 'View/Reset' : 'Generate'}
                      </Button>
                    </div>
                  ),
                },
                {
                  title: 'Permissions',
                  render: (_, record) => (
                    <div className="flex flex-wrap gap-1">
                      {(record.allowed_sections || ['dashboard']).slice(0, 3).map((section: string) => (
                        <Tag key={section} color="purple" className="text-xs">
                          {section}
                        </Tag>
                      ))}
                      {(record.allowed_sections || []).length > 3 && (
                        <Tag color="default" className="text-xs">
                          +{(record.allowed_sections || []).length - 3}
                        </Tag>
                      )}
                    </div>
                  ),
                },
                {
                  title: 'Actions',
                  width: 120,
                  render: (_, record) => (
                    <Button 
                      size="small"
                      onClick={() => handleOpenPermissions(record)}
                    >
                      Edit Access
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </section>

        {/* Access Key Modal */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <KeyIcon className="h-5 w-5 text-purple-600" />
              <span>Staff Access Key</span>
            </div>
          }
          open={accessKeyModalOpen}
          onCancel={() => setAccessKeyModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setAccessKeyModalOpen(false)}>
              Close
            </Button>,
            <Button key="regenerate" type="default" onClick={handleRegenerateKey}>
              {generatedKey ? 'Regenerate Key' : 'Generate Key'}
            </Button>,
          ]}
        >
          {selectedStaff && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {selectedStaff.first_name} {selectedStaff.last_name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{selectedStaff.email}</p>
              </div>
              
              {generatedKey ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Share this access key with the Staff. They will use it to log in to the system.
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl text-lg font-mono font-bold text-purple-700 dark:text-purple-300 tracking-widest text-center">
                      {generatedKey}
                    </code>
                    <Button type="primary" onClick={handleCopyKey}>
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    ⚠️ This key is shown only once. If regenerated, the old key will stop working.
                  </p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No access key has been generated for this Staff yet.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Click "Generate Key" to create one.
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Permissions Modal */}
        <Modal
          title={
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5 text-purple-600" />
              <span>Edit Staff Permissions</span>
            </div>
          }
          open={permissionsModalOpen}
          onCancel={() => setPermissionsModalOpen(false)}
          onOk={handleSavePermissions}
          okText="Save Permissions"
        >
          {selectedStaff && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {selectedStaff.first_name} {selectedStaff.last_name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{selectedStaff.email}</p>
              </div>
              
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Select the sections this Staff can access:
                </p>
                <div className="space-y-2">
                  {availableSections.map((section) => (
                    <div 
                      key={section.key}
                      className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition ${
                        editingPermissions.includes(section.key)
                          ? 'border-purple-300 bg-purple-50 dark:border-purple-700 dark:bg-purple-900/20'
                          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
                      }`}
                      onClick={() => togglePermission(section.key)}
                    >
                      <div>
                        <p className="text-sm font-medium">{section.label}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{section.description}</p>
                      </div>
                      <Checkbox 
                        checked={editingPermissions.includes(section.key)}
                        disabled={section.key === 'dashboard'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title={editingRoom ? 'Edit Studio Room' : 'Add Studio Room'}
          open={roomModalOpen}
          onCancel={() => {
            setRoomModalOpen(false);
            setEditingRoom(null);
            roomForm.resetFields();
          }}
          onOk={handleSaveRoom}
          okText={editingRoom ? 'Update Room' : 'Add Room'}
        >
          <Form form={roomForm} layout="vertical">
            <Form.Item
              name="name"
              label="Room Name"
              rules={[{ required: true, message: 'Please enter room name' }]}
            >
              <Input placeholder="e.g. Main Room A" size="large" />
            </Form.Item>
          </Form>
        </Modal>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-200">
                <DocumentTextIcon className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Professional PDF reports</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">Generate monthly expense, payment, and financial snapshots.</p>
              </div>
            </div>
            <Button type="primary" size="large" onClick={handleGenerateReport} loading={isLoadingData}>
              Generate PDF
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_2fr]">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Report month</label>
              <Input
                type="month"
                className="mt-2"
                size="large"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
              />
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Choose the month you want to summarize. Reports will consolidate transactions from that month.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {defaultReports.map((report) => {
                const active = selectedReports.includes(report.key);
                return (
                  <button
                    key={report.key}
                    type="button"
                    onClick={() => {
                      setSelectedReports((prev) =>
                        prev.includes(report.key)
                          ? prev.filter((item) => item !== report.key)
                          : [...prev, report.key]
                      );
                    }}
                    className={`rounded-2xl border px-4 py-4 text-left transition ${
                      active
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm dark:border-indigo-400 dark:bg-indigo-500/10 dark:text-indigo-100'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300'
                    }`}
                  >
                    <p className="text-sm font-semibold">{report.label}</p>
                    <p className="mt-2 text-xs opacity-70">Included in PDF export</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4 text-sm dark:border-slate-800 dark:bg-slate-950">
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Payments</p>
              <p className="mt-2 text-lg font-semibold">{formatCurrencySimple(reportTotals.totalPayments)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{reportPayments.length} transactions</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4 text-sm dark:border-slate-800 dark:bg-slate-950">
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Expenses</p>
              <p className="mt-2 text-lg font-semibold">{formatCurrencySimple(reportTotals.totalExpenses)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{reportExpenses.length} entries</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4 text-sm dark:border-slate-800 dark:bg-slate-950">
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Net position</p>
              <p className="mt-2 text-lg font-semibold">{formatCurrencySimple(reportTotals.net)}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Across {reportProjects.length} projects</p>
            </div>
          </div>
        </section>
      
        {/* --- STAFF PAYMENTS SECTION --- */}
        <section className="rounded-2xl mt-12 border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex justify-between">
            <h2 className="text-lg font-semibold">Staff & Producer Payments</h2>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenPaymentModal}>Record Payment</Button>
          </div>
          <Table rowKey="id" loading={isLoadingData} dataSource={staffPayments} pagination={{ pageSize: 6 }}
            columns={[
              { title: 'Recipient', render: (_, r) => { const s: any = Staffs.find((st: any) => st.id === r.recipient); return <span>{r.recipient_full_name || getStaffDisplayName(s)}</span>; } },
              { title: 'Amount', render: (_, r) => <b>{formatCurrencySimple(r.amount, r.currency_code)}</b> },
              { title: 'Month/Year', dataIndex: 'month_year' },
              { title: 'Notes', dataIndex: 'notes' },
              { title: 'Status', render: (_, r) => { const c:any = {approved:'green', pending:'orange', rejected:'red'}; return <Tag color={c[r.status]}>{r.status?.toUpperCase()}</Tag>} },
              {
                title: 'Receipt',
                render: (_, r) => {
                  const s: any = Staffs.find((st: any) => Number(st.id) === Number(r.recipient));
                  return (
                    <Button
                      type="link"
                      icon={<FilePdfOutlined />}
                      onClick={() => downloadStaffPaymentReceiptPdf(r, {
                        studioName: user?.studio_name,
                        recipientName: r.recipient_full_name || getStaffDisplayName(s),
                        paidToDate: r.paid_to_date ?? getApprovedPayoutTotal(Number(r.recipient)),
                        remainingBalance: r.remaining_balance ?? getStaffRemainingAmount(s),
                      })}
                    >
                      Download PDF
                    </Button>
                  );
                }
              }
            ]} />
        </section>

        <Modal title="Record Staff Payment" open={staffPaymentModalOpen} onCancel={() => setStaffPaymentModalOpen(false)} onOk={handleSubmitPayment} confirmLoading={isSubmittingPayment}>
          <Form form={paymentForm} layout="vertical">
            <Form.Item name="staff_id" label="Recipient" rules={[{ required: true }]}>
              <Select
                placeholder="Select staff or producer"
                onChange={(value) => {
                  const staff = Staffs.find((item: any) => Number(item.id) === Number(value));
                  applyPaymentPortion(paymentForm.getFieldValue('payment_portion') || 'custom', staff);
                }}
              >
                {Staffs.map((s:any) => (
                  <Select.Option key={s.id} value={s.id}>
                    {getStaffDisplayName(s)} - remaining {formatCurrencySimple(getStaffRemainingAmount(s), getStaffCurrencyCode(s))}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {selectedPaymentStaff && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                <div className="flex items-center justify-between gap-3">
                  <span>Total owed</span>
                  <strong>{formatCurrencySimple(getStaffOwedAmount(selectedPaymentStaff), selectedPaymentCurrency)}</strong>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span>Already approved</span>
                  <strong>{formatCurrencySimple(getApprovedPayoutTotal(Number(selectedPaymentStaff.id)), selectedPaymentCurrency)}</strong>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3 text-slate-900">
                  <span>Remaining</span>
                  <strong>{formatCurrencySimple(selectedPaymentRemaining, selectedPaymentCurrency)}</strong>
                </div>
              </div>
            )}
            <Form.Item name="payment_portion" label="Payment Option" initialValue="custom">
              <Select
                options={[
                  { value: 'half', label: 'Pay Half of Remaining' },
                  { value: 'full', label: 'Pay Full Remaining' },
                  { value: 'custom', label: 'Custom Amount' },
                ]}
                onChange={(value) => applyPaymentPortion(value)}
              />
            </Form.Item>
            <Form.Item name="amount" label={`Amount (${selectedPaymentCurrency})`} rules={[{ required: true }]}>
              <InputNumber min={0} max={selectedPaymentRemaining} precision={selectedPaymentCurrency === 'USD' ? 2 : 0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="month_year" label="Month/Year"><Input placeholder="MM/YYYY" /></Form.Item>
            <Form.Item name="notes" label="Notes / Project Name"><Input /></Form.Item>
            <div className="rounded-xl bg-indigo-50 p-4 text-sm text-indigo-700">
              SOMS will generate a professional receipt automatically after this payment is recorded.
            </div>
          </Form>
        </Modal>
      </main>
    </div>
  );
};

export default Management;
